// Branch vertices straight off the part meshes' own skinning — no association, no
// nearest-neighbour search.
//
// A part GLB and its trained branch are the SAME geometry. The GLB is non-indexed (one
// position per triangle corner) and the branch mesh's `faces` entry at corner c IS that
// corner's branch-vertex index, so the remap is a scatter:
//
//     branchVertex[ faces[c] ] = skinnedPartCorner[c]
//
// Corners sharing a branch vertex must land on the same position. `maxDisagreement`
// reports that spread — near zero proves the part ORDER matches what the branch was
// trained on, and a large value means the scatter is binding unrelated vertices.
//
// FRAME, AND IT IS PER PART. A part GLB carries the artist's FBX frame — centimetres,
// Z up — while a trained branch mesh is metres in the export's own frame, which the
// manifest's world_rotation turns upright at render time. Training solves that conversion
// SEPARATELY FOR EACH PART (build_v5_meshes.py's get_part: a signed axis permutation plus
// a translation, frozen at pose 0), so one shared conversion is right for whichever part
// it was derived from and offset for the rest — head by 55 mm, sweater 32 mm, body 22 mm.
// On a 1.2 m torso 22 mm hides inside the bind-vs-neutral pose difference and the body
// looks fine; on a 0.33 m head 55 mm does not. So each part solves its own
// (partFrameFit.js) and `neutralResidual` reports what it achieved: maxDisagreement
// compares corners only against each other, so it reads ~0 even when every vertex is in
// the wrong frame entirely.

import * as THREE from 'three'
import { PART_FIT_MAX_RESIDUAL_METRES, applyPartFrame, fitPartFrame } from './partFrameFit.js'

/** Part meshes per branch, in the order their corners concatenate. */
export const BRANCH_PARTS = {
  head: ['SKM_MHC_Myra_FaceMesh'],
  body: ['SKM_MHC_Myra_BodyMesh'],
  clothes: [
    'SK_sweaterRollerSkate_sweater',
    'SK_sweaterRollerSkate_pants',
    'SK_sweaterRollerSkate_sneakers',
  ],
}

const CENTIMETRES_TO_METRES = 0.01

/** The axis convention every part solves to, as a matrix — for placing a driver mesh
 *  beside the Gaussians. Rotation and scale only: each part's own TRANSLATION is what
 *  differs, so this places the rig approximately and the per-part fits place the splats. */
export function partToBranchMatrix() {
  const scale = CENTIMETRES_TO_METRES
  return new THREE.Matrix4().set(
    0, -scale, 0, 0,
    0, 0, -scale, 0,
    scale, 0, 0, 0,
    0, 0, 0, 1)
}

/** Skinned position of one corner, converted by that part's OWN solved fit — the REFERENCE
 *  skin (three's own), kept for the load-time diagnostics; `deform` is its fast twin. */
function skinnedCorner(mesh, index, fit, target) {
  target.fromBufferAttribute(mesh.geometry.attributes.position, index)
  mesh.applyBoneTransform(index, target)
  return applyPartFrame(fit, target)
}

/** Each part's solved FBX→branch fit, in corner order, refusing a fit that isn't one. */
function fitParts(branch, meshes, partNames, target) {
  const fits = []
  let firstCorner = 0
  for (let index = 0; index < meshes.length; index++) {
    const positions = meshes[index].geometry.attributes.position
    // The BIND pose: applyBoneTransform is the identity there, so the raw attribute is
    // the pose training solved against (get_part's `p.skin(p.pose_of(0))`).
    const corners = positions.array.subarray(0, positions.count * 3)
    const fit = fitPartFrame(corners, target.faces, firstCorner, target.neutralVertices)
    if (fit.residualMean > PART_FIT_MAX_RESIDUAL_METRES) {
      throw new Error(
        `partBranchVertices: branch '${branch}' part '${partNames[index]}' fits its trained `
        + `vertices no better than ${(fit.residualMean * 1000).toFixed(0)}mm — that is not a `
        + 'frame convention, it is the wrong part or the wrong corner order')
    }
    fits.push({ name: partNames[index], ...fit })
    firstCorner += positions.count
  }
  return fits
}

/** Per-vertex offset landing the BIND pose exactly on the branch's trained neutral.
 *
 *  The fit is exact for the head and 21.6 mm out for the body — a POSE difference no
 *  global transform absorbs — which makes it a ~25 mm SEAM error at the neck, not a
 *  cosmetic one. Same instrument as OGSBranch.pose's `corr`.
 *  Notes: ~/comments/aos-threejs-poc/partBranchVertices_bindCorrection_seam_error.md */
function bindCorrection(meshes, target, fits) {
  const corr = new Float32Array(target.manifest.num_vertices * 3)
  const point = new THREE.Vector3()
  let corner = 0
  for (let part = 0; part < meshes.length; part++) {
    const positions = meshes[part].geometry.attributes.position
    // applyBoneTransform is the identity at bind, so the raw attribute IS the bind pose.
    for (let index = 0; index < positions.count; index++, corner++) {
      point.set(positions.array[index * 3], positions.array[index * 3 + 1], positions.array[index * 3 + 2])
      applyPartFrame(fits[part], point)
      const vertex = target.faces[corner] * 3
      corr[vertex] = target.neutralVertices[vertex] - point.x
      corr[vertex + 1] = target.neutralVertices[vertex + 1] - point.y
      corr[vertex + 2] = target.neutralVertices[vertex + 2] - point.z
    }
  }
  return corr
}

/** The first corner naming each branch vertex: `(part, index)` per vertex. A non-indexed part
 *  names a vertex once per face it touches (~6x), and skinning it once is enough. */
function representativeCorners(meshes, faces, numVertices) {
  const part = new Uint8Array(numVertices)
  const index = new Uint32Array(numVertices)
  const seen = new Uint8Array(numVertices)
  let corner = 0
  for (let p = 0; p < meshes.length; p++) {
    const count = meshes[p].geometry.attributes.position.count
    for (let i = 0; i < count; i++, corner++) {
      const vertex = faces[corner]
      if (seen[vertex]) continue
      seen[vertex] = 1
      part[vertex] = p
      index[vertex] = i
    }
  }
  return { part, index }
}

/** The per-vertex attributes `deform` reads raw; a normalised integer attribute would need
 *  the slow decoding path, and our own compose tool never writes one. */
function skinAttributes(mesh, name) {
  const { skinIndex, skinWeight } = mesh.geometry.attributes
  for (const [label, attr] of [['skinIndex', skinIndex], ['skinWeight', skinWeight]]) {
    if (!attr) throw new Error(`partBranchVertices: part '${name}' has no ${label} attribute`)
    if (attr.itemSize !== 4 || attr.normalized) {
      throw new Error(`partBranchVertices: part '${name}' ${label} must be 4 raw components per vertex`)
    }
  }
  return { skinIndex: skinIndex.array, skinWeight: skinWeight.array }
}

/** Every bone's `matrixWorld x boneInverse x bindMatrix`, 16 floats each — what three's
 *  applyBoneTransform rebuilds per VERTEX, built here once per part per frame. */
function skinMatrices(mesh, out) {
  const { bones, boneInverses } = mesh.skeleton
  for (let b = 0; b < bones.length; b++) {
    SKIN_SCRATCH.multiplyMatrices(bones[b].matrixWorld, boneInverses[b]).multiply(mesh.bindMatrix)
    out.set(SKIN_SCRATCH.elements, b * 16)
  }
  return out
}

const SKIN_SCRATCH = new THREE.Matrix4()

export class PartBranchVertices {
  /**
   * @param {Object} partMeshes    { [partName]: SkinnedMesh }, keyed on the part file name
   * @param {Object} branchMeshes  { [branch]: { faces, neutralVertices, manifest } }
   * @param {Object} branchParts   branch → part names, in corner-concatenation order
   */
  constructor(partMeshes, branchMeshes, branchParts = BRANCH_PARTS) {
    this.branches = {}
    for (const [branch, partNames] of Object.entries(branchParts)) {
      const target = branchMeshes[branch]
      if (!target) continue
      const meshes = partNames.map((name) => {
        const mesh = partMeshes[name]
        if (!mesh) throw new Error(`partBranchVertices: no part mesh '${name}' for branch '${branch}'`)
        return mesh
      })
      const corners = meshes.reduce((sum, mesh) => sum + mesh.geometry.attributes.position.count, 0)
      if (corners !== target.faces.length) {
        throw new Error(
          `partBranchVertices: branch '${branch}' has ${corners} part corners but the trained `
          + `mesh has ${target.faces.length} (${target.faces.length / 3} faces) — not the same geometry`)
      }
      const fits = fitParts(branch, meshes, partNames, target)
      this.branches[branch] = {
        meshes,
        faces: target.faces,
        neutralVertices: target.neutralVertices,
        fits,
        out: new Float32Array(target.manifest.num_vertices * 3),
        corr: bindCorrection(meshes, target, fits),
        representatives: representativeCorners(meshes, target.faces, target.manifest.num_vertices),
        // Per part: the raw skin attributes and a per-bone matrix buffer, reused every frame.
        skins: meshes.map((mesh, p) => ({
          ...skinAttributes(mesh, partNames[p]),
          positions: mesh.geometry.attributes.position.array,
          matrices: new Float32Array(mesh.skeleton.bones.length * 16),
          bindInverse: mesh.bindMatrixInverse.elements,
        })),
      }
    }
    this._point = new THREE.Vector3()
  }

  /** Skin the parts at their current pose and scatter into branch-vertex order.
   *  Each vertex ONCE (not per corner), with each bone's skin matrix built ONCE per frame:
   *  the same arithmetic as three's applyBoneTransform, without redoing it 900k times.
   *  Notes: ~/comments/aos-threejs-poc/partBranchVertices_deform_per_vertex_skinning.md */
  deform(branch) {
    const state = this.branches[branch]
    if (!state) return null
    const { meshes, fits, out, corr, representatives, skins } = state
    for (let p = 0; p < meshes.length; p++) skinMatrices(meshes[p], skins[p].matrices)
    const count = out.length / 3
    for (let vertex = 0; vertex < count; vertex++) {
      const skin = skins[representatives.part[vertex]]
      const i = representatives.index[vertex]
      const { positions, skinIndex, skinWeight, matrices, bindInverse } = skin
      const x = positions[i * 3]
      const y = positions[i * 3 + 1]
      const z = positions[i * 3 + 2]
      // Σ weight · (bone matrix · p), as three does — bindMatrix is folded into each bone.
      let ax = 0, ay = 0, az = 0
      for (let k = 0; k < 4; k++) {
        const w = skinWeight[i * 4 + k]
        if (w === 0) continue
        const m = skinIndex[i * 4 + k] * 16
        ax += w * (matrices[m] * x + matrices[m + 4] * y + matrices[m + 8] * z + matrices[m + 12])
        ay += w * (matrices[m + 1] * x + matrices[m + 5] * y + matrices[m + 9] * z + matrices[m + 13])
        az += w * (matrices[m + 2] * x + matrices[m + 6] * y + matrices[m + 10] * z + matrices[m + 14])
      }
      // Back through bindMatrixInverse (three's last step), then the part's own frame.
      const e = bindInverse
      const sx = e[0] * ax + e[4] * ay + e[8] * az + e[12]
      const sy = e[1] * ax + e[5] * ay + e[9] * az + e[13]
      const sz = e[2] * ax + e[6] * ay + e[10] * az + e[14]
      const { axes, signs } = fits[representatives.part[vertex]].permutation
      const t = fits[representatives.part[vertex]].translation
      const base = vertex * 3
      out[base] = signs[0] * component(sx, sy, sz, axes[0]) * CENTIMETRES_TO_METRES + t[0] + corr[base]
      out[base + 1] = signs[1] * component(sx, sy, sz, axes[1]) * CENTIMETRES_TO_METRES + t[1] + corr[base + 1]
      out[base + 2] = signs[2] * component(sx, sy, sz, axes[2]) * CENTIMETRES_TO_METRES + t[2] + corr[base + 2]
    }
    return out
  }

  /** Everything the GPU skinner needs for `branch`, packed once (GpuPartSkinner).
   *
   *  Built from the SAME `representatives` list `deform` walks — the one corner that
   *  speaks for each branch vertex — so the two implementations cannot disagree about
   *  which corner that is. Every other input is static: the bind positions, the 4-bone
   *  indices/weights, each part's solved frame, the bind correction. Only the bone
   *  matrices move, and those are 76 KB a frame. Null for a branch this instance does
   *  not carry.
   *
   *  One entry per branch vertex, in vertex order, so `meta[i * 2 + 1] === i` — kept
   *  explicit rather than implied, because the shader reads it and a future list that is
   *  not vertex-ordered would still be correct. */
  skinPlan(branch) {
    const state = this.branches[branch]
    if (!state) return null
    const { meshes, fits, corr, representatives, out } = state
    const n = out.length / 3
    const bindPos = new Float32Array(n * 3)
    const skinIndex = new Uint32Array(n * 4)
    const skinWeight = new Float32Array(n * 4)
    const meta = new Uint32Array(n * 2)
    for (let vertex = 0; vertex < n; vertex++) {
      const p = representatives.part[vertex]
      const index = representatives.index[vertex]
      const mesh = meshes[p]
      const pos = mesh.geometry.attributes.position.array
      bindPos[vertex * 3] = pos[index * 3]
      bindPos[vertex * 3 + 1] = pos[index * 3 + 1]
      bindPos[vertex * 3 + 2] = pos[index * 3 + 2]
      const si = mesh.geometry.attributes.skinIndex
      const sw = mesh.geometry.attributes.skinWeight
      for (let k = 0; k < 4; k++) {
        skinIndex[vertex * 4 + k] = si.array[index * si.itemSize + k]
        skinWeight[vertex * 4 + k] = sw.array[index * sw.itemSize + k]
      }
      meta[vertex * 2] = p
      meta[vertex * 2 + 1] = vertex
    }
    // Parts SHARE a skeleton in rig.glb (one bone hierarchy for the whole rig), so the
    // bone matrices are uploaded once per distinct skeleton and each part names its offset.
    const skeletons = []
    const parts = meshes.map((mesh, index) => {
      let slot = skeletons.indexOf(mesh.skeleton)
      if (slot < 0) { slot = skeletons.length; skeletons.push(mesh.skeleton) }
      return {
        bindMatrix: mesh.bindMatrix.elements,
        bindMatrixInverse: mesh.bindMatrixInverse.elements,
        // The cm→m scale rides on the sign, exactly as applyPartFrame folds it in.
        axes: fits[index].permutation.axes,
        signs: fits[index].permutation.signs.map((sign) => sign * CENTIMETRES_TO_METRES),
        translation: fits[index].translation,
        skeleton: slot,
      }
    })
    return { count: n, bindPos, skinIndex, skinWeight, meta, parts, skeletons, corr, vertexCount: n }
  }

  /** Each part's solved fit for `branch` — the frame contract, for the log/panel. */
  partFits(branch) {
    return this.branches[branch]?.fits ?? null
  }

  /** The solved fit of ONE part, by name, wherever it sits. A transform measured in a
   *  part's own space — the head bone's rigid delta, say — has to be conjugated by THAT
   *  part's fit; the shared partToBranchMatrix carries no translation, and conjugating a
   *  rotation about the wrong origin displaces by (I - R)·offset, which is nothing at rest
   *  and grows with the angle. */
  fitForPart(name) {
    for (const state of Object.values(this.branches)) {
      const hit = state?.fits?.find((fit) => fit.name === name)
      if (hit) return hit
    }
    return null
  }

  /** Distance from the CURRENT pose to the branch's trained neutral, metres.
   *
   *  Read at the bind pose this is the frame check: the part GLB and the branch are the
   *  same geometry, so a correct conversion lands within the bind-vs-neutral pose
   *  difference (centimetres) while a wrong axis or scale lands a metre out. */
  neutralResidual(branch) {
    const state = this.branches[branch]
    if (!state) return null
    const posed = this.deform(branch)
    const neutral = state.neutralVertices
    const count = neutral.length / 3
    let worst = 0
    let sum = 0
    for (let vertex = 0; vertex < count; vertex++) {
      const base = vertex * 3
      const distance = Math.hypot(
        posed[base] - neutral[base],
        posed[base + 1] - neutral[base + 1],
        posed[base + 2] - neutral[base + 2])
      sum += distance
      if (distance > worst) worst = distance
    }
    return { mean: sum / count, max: worst }
  }

  /** Largest spread between corners sharing a branch vertex, in metres. */
  maxDisagreement(branch) {
    const state = this.branches[branch]
    if (!state) return null
    const { meshes, faces, fits, out } = state
    const seen = new Float32Array(out.length)
    const written = new Uint8Array(out.length / 3)
    const point = this._point
    let worst = 0
    let corner = 0
    for (let part = 0; part < meshes.length; part++) {
      const mesh = meshes[part]
      const fit = fits[part]
      const count = mesh.geometry.attributes.position.count
      for (let index = 0; index < count; index++, corner++) {
        skinnedCorner(mesh, index, fit, point)
        const vertex = faces[corner]
        const base = vertex * 3
        if (written[vertex]) {
          worst = Math.max(worst,
            Math.abs(seen[base] - point.x),
            Math.abs(seen[base + 1] - point.y),
            Math.abs(seen[base + 2] - point.z))
          continue
        }
        written[vertex] = 1
        seen[base] = point.x
        seen[base + 1] = point.y
        seen[base + 2] = point.z
      }
    }
    return worst
  }
}

/** Component `axis` of (x, y, z) — applyPartFrame's permutation, without its array. */
function component(x, y, z, axis) {
  return axis === 0 ? x : (axis === 1 ? y : z)
}
